/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Single search view used for BOTH the global marketplace (main domain) and a tenant
 * storefront (subdomain / custom domain). The backend scopes results by the request host,
 * so the component does not need to pass an owner id — only the hero copy differs.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Car, CarSearchRequest } from '../types';
import api from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { categoryLabel } from '../utils/localized';
import SearchBar from '../components/SearchBar';
import ModelCategoryList from '../components/ModelCategoryList';
import CarCategoryList from '../components/CarCategoryList';
import ViewModeToggle, { useViewMode } from '../components/ViewModeToggle';

export default function HomePage() {
  const { t, i18n } = useTranslation();
  const { tenant, isMainDomain } = useTenant();
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [rentalType, setRentalType] = useState(0); // last searched rental period (for price unit)
  const [viewMode, setViewMode] = useViewMode('models');
  const [category, setCategory] = useState('');
  const [searchCity, setSearchCity] = useState(''); // last searched city — scopes the model-booking resolve

  // Category chips/labels are localized, so clear the selected-category filter on language change
  // (the previously-selected label no longer matches the new language's labels).
  useEffect(() => { setCategory(''); }, [i18n.language]);

  // Distinct categories from the loaded fleet (CarCategories segment), in catalog order.
  const categories = useMemo(() => {
    const order = new Map<string, number>();
    for (const c of cars) {
      const name = categoryLabel(c, i18n.language);
      if (!name) continue;
      order.set(name, Math.min(order.get(name) ?? 99, c.categoryPosition ?? 99));
    }
    return Array.from(order.entries())
      .sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]))
      .map(([name]) => name);
  }, [cars, i18n.language]);

  // Results narrowed to the selected category (applied client-side).
  const visible = useMemo(
    () =>
      cars.filter(
        (c) =>
          !category ||
          categoryLabel(c, i18n.language).toLowerCase() === category.toLowerCase(),
      ),
    [cars, category, i18n.language],
  );

  const runSearch = async (req: CarSearchRequest) => {
    setLoading(true);
    setRentalType(req.rentalType);
    setSearchCity(req.city || '');
    try {
      const results = await api.search(req);
      setCars(results);
    } catch {
      setCars([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  // Initial load: show available cars right away.
  useEffect(() => {
    const now = new Date();
    runSearch({
      // No geo filter — browse the whole catalogue (many cars have no location).
      latitude: 0,
      longitude: 0,
      dateFrom: now.toISOString(),
      dateTo: new Date(now.getTime() + 7 * 864e5).toISOString(),
      rentalType: 0,
      distance: 0,
      typesIds: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
    <div className="max-w-6xl mx-auto px-4">
      <div className="mt-8 px-2">
        <SearchBar
          onSearch={runSearch}
          categories={categories}
          category={category}
          onCategoryChange={setCategory}
          loading={loading}
        />
      </div>
    </div>

    {/* Results — light section (black text on white field), same look as the tenant page. */}
    <section className="bg-gray-50 mt-10 py-12 min-h-[60vh]">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h2 className="font-bold text-2xl text-gray-900">
            {isMainDomain
              ? t('results.availableCars')
              : t('results.carsFrom', { name: tenant?.displayName || t('results.thisHost') })}
            {searched && !loading && <span className="text-gray-500 font-normal"> · {visible.length}</span>}
          </h2>
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500">{t('results.loading')}</div>
        ) : visible.length === 0 ? (
          <div className="py-16 text-center text-gray-500">{t('results.noneFound')}</div>
        ) : viewMode === 'cars' ? (
          <CarCategoryList cars={visible} />
        ) : (
          <ModelCategoryList cars={visible} city={searchCity} />
        )}
      </div>
    </section>
    </>
  );
}
